import React, { useRef, useMemo, Suspense, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Stars, Float } from '@react-three/drei';
import * as THREE from 'three';

interface SatelliteConfig {
  radius: number;
  speed: number;
  phase: number;
  tiltX: number;
  tiltZ: number;
  scale: number;
  bodyColor: string;
  panelColor: string;
  accentColor: string;
  pingOffset: number;
}

const SATS: SatelliteConfig[] = [
  { radius: 3.2, speed: 0.35, phase: 0,   tiltX: 0.2,   tiltZ: 0.0,  scale: 0.55, bodyColor: '#d8dde5', panelColor: '#1a4060', accentColor: '#5ce1e6', pingOffset: 0   },
  { radius: 4.6, speed: 0.22, phase: 1.4, tiltX: -0.35, tiltZ: 0.3,  scale: 0.7,  bodyColor: '#c8ced8', panelColor: '#0d2a4a', accentColor: '#33bbcf', pingOffset: 0.8 },
  { radius: 5.8, speed: 0.16, phase: 2.6, tiltX: 0.55,  tiltZ: -0.2, scale: 0.48, bodyColor: '#e4e8ee', panelColor: '#143358', accentColor: '#a8f0ff', pingOffset: 1.6 },
  { radius: 4.1, speed: 0.28, phase: 4.1, tiltX: -0.15, tiltZ: 0.5,  scale: 0.6,  bodyColor: '#d0d5de', panelColor: '#102a48', accentColor: '#5ce1e6', pingOffset: 2.4 },
  { radius: 6.8, speed: 0.12, phase: 5.5, tiltX: 0.4,   tiltZ: -0.4, scale: 0.62, bodyColor: '#cfd4dc', panelColor: '#0a2a50', accentColor: '#a8f0ff', pingOffset: 3.2 },
];


interface SharedRefs {
  mouse: React.MutableRefObject<{ x: number; y: number }>;
  scrollY: React.MutableRefObject<number>;
  easter: React.MutableRefObject<number>; // 0-1 boost factor
  reducedMotion: React.MutableRefObject<boolean>;
  satPositions: React.MutableRefObject<THREE.Vector3[]>;
}


const SatelliteMesh: React.FC<{ cfg: SatelliteConfig; pingRef: React.MutableRefObject<number> }> = ({ cfg, pingRef }) => {
  const tipRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    if (tipRef.current) {
      const mat = tipRef.current.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 0.6 + pingRef.current * 3.5;
      const s = 1 + pingRef.current * 2;
      tipRef.current.scale.setScalar(s);
    }
  });

  return (
    <group scale={cfg.scale}>
      <mesh>
        <boxGeometry args={[0.5, 0.4, 0.5]} />
        <meshStandardMaterial color={cfg.bodyColor} flatShading roughness={0.5} metalness={0.6} />
      </mesh>

      <mesh position={[0, 0.05, 0.251]}>
        <planeGeometry args={[0.45, 0.08]} />
        <meshStandardMaterial color={cfg.accentColor} emissive={cfg.accentColor} emissiveIntensity={0.8} flatShading />
      </mesh>
      <mesh position={[0, 0.05, -0.251]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[0.45, 0.08]} />
        <meshStandardMaterial color={cfg.accentColor} emissive={cfg.accentColor} emissiveIntensity={0.8} flatShading />
      </mesh>

      <mesh position={[-0.35, 0, 0]}>
        <boxGeometry args={[0.2, 0.05, 0.05]} />
        <meshStandardMaterial color="#8890a0" flatShading metalness={0.7} />
      </mesh>
      <mesh position={[0.35, 0, 0]}>
        <boxGeometry args={[0.2, 0.05, 0.05]} />
        <meshStandardMaterial color="#8890a0" flatShading metalness={0.7} />
      </mesh>

      <group position={[-0.85, 0, 0]}>
        <mesh>
          <boxGeometry args={[0.8, 0.02, 0.45]} />
          <meshStandardMaterial color={cfg.panelColor} flatShading metalness={0.85} roughness={0.25} />
        </mesh>
        <mesh position={[0, 0.012, 0]}>
          <planeGeometry args={[0.78, 0.43]} />
          <meshStandardMaterial color={cfg.panelColor} emissive={cfg.accentColor} emissiveIntensity={0.15} flatShading />
        </mesh>
      </group>
      <group position={[0.85, 0, 0]}>
        <mesh>
          <boxGeometry args={[0.8, 0.02, 0.45]} />
          <meshStandardMaterial color={cfg.panelColor} flatShading metalness={0.85} roughness={0.25} />
        </mesh>
        <mesh position={[0, 0.012, 0]}>
          <planeGeometry args={[0.78, 0.43]} />
          <meshStandardMaterial color={cfg.panelColor} emissive={cfg.accentColor} emissiveIntensity={0.15} flatShading />
        </mesh>
      </group>

      <mesh position={[0, 0.3, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[0.15, 0.1, 8, 1, true]} />
        <meshStandardMaterial color="#e0e4ea" flatShading side={THREE.DoubleSide} metalness={0.7} />
      </mesh>
      {/* Dish tip — bip */}
      <mesh ref={tipRef} position={[0, 0.42, 0]}>
        <sphereGeometry args={[0.04, 12, 12]} />
        <meshStandardMaterial color={cfg.accentColor} emissive={cfg.accentColor} emissiveIntensity={0.6} />
      </mesh>

      <mesh position={[0, 0, -0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.2, 6]} />
        <meshStandardMaterial color="#9098a8" flatShading />
      </mesh>
    </group>
  );
};


const TRAIL_POINTS = 40;

const Satellite: React.FC<{ cfg: SatelliteConfig; index: number; shared: SharedRefs }> = ({ cfg, index, shared }) => {
  const ref = useRef<THREE.Group>(null);
  const basePos = useRef(new THREE.Vector3());
  const pingRef = useRef(0);

  const trailGeom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const positions = new Float32Array(TRAIL_POINTS * 3);
    const colors = new Float32Array(TRAIL_POINTS * 3);
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return g;
  }, []);

  const trailRef = useRef<THREE.Line>(null);
  const trailInit = useRef(false);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    const eSpeed = 1 + shared.easter.current * 4;
    const eRad = 1 + shared.easter.current * 1.8;
    const angle = cfg.phase + t * cfg.speed * eSpeed;
    const r = cfg.radius * eRad;

    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r * 0.75;
    const y = Math.sin(angle * 1.3 + cfg.phase) * 0.4;

    const cosX = Math.cos(cfg.tiltX), sinX = Math.sin(cfg.tiltX);
    const cosZ = Math.cos(cfg.tiltZ), sinZ = Math.sin(cfg.tiltZ);
    let px = x;
    let py = y * cosX - z * sinX;
    let pz = y * sinX + z * cosX;
    const px2 = px * cosZ - py * sinZ;
    const py2 = px * sinZ + py * cosZ;
    px = px2; py = py2;

    basePos.current.set(px, py, pz);

    if (!shared.reducedMotion.current) {
      const mx = shared.mouse.current.x * 3.5;
      const my = shared.mouse.current.y * 2.0;
      const dx = mx - px;
      const dy = my - py;
      const dist2 = dx * dx + dy * dy + 1;
      const pull = 1.2 / dist2;
      basePos.current.x += dx * pull;
      basePos.current.y += dy * pull;

      const s = shared.scrollY.current;
      basePos.current.y -= s * 2.5;
      basePos.current.x *= 1 + s * 0.4;
      basePos.current.z -= s * 1.5;
    }

    ref.current.position.lerp(basePos.current, shared.reducedMotion.current ? 1 : 0.12);
    ref.current.lookAt(0, 0, 0);
    ref.current.rotateY(Math.PI / 2);
    ref.current.rotateZ(Math.sin(t * 0.8 + cfg.phase) * 0.08);

    shared.satPositions.current[index].copy(ref.current.position);

    // Ping cycle 3s
    const cycle = ((t + cfg.pingOffset) % 3) / 3;
    pingRef.current = cycle < 0.15 ? Math.sin(cycle / 0.15 * Math.PI) : 0;

    // Trail update
    const posAttr = trailGeom.attributes.position as THREE.BufferAttribute;
    const colAttr = trailGeom.attributes.color as THREE.BufferAttribute;
    const arr = posAttr.array as Float32Array;
    const carr = colAttr.array as Float32Array;

    if (!trailInit.current) {
      for (let i = 0; i < TRAIL_POINTS; i++) {
        arr[i * 3]     = ref.current.position.x;
        arr[i * 3 + 1] = ref.current.position.y;
        arr[i * 3 + 2] = ref.current.position.z;
      }
      trailInit.current = true;
    } else {
      // shift
      for (let i = TRAIL_POINTS - 1; i > 0; i--) {
        arr[i * 3]     = arr[(i - 1) * 3];
        arr[i * 3 + 1] = arr[(i - 1) * 3 + 1];
        arr[i * 3 + 2] = arr[(i - 1) * 3 + 2];
      }
      arr[0] = ref.current.position.x;
      arr[1] = ref.current.position.y;
      arr[2] = ref.current.position.z;
    }

    const accent = new THREE.Color(cfg.accentColor);
    for (let i = 0; i < TRAIL_POINTS; i++) {
      const a = 1 - i / TRAIL_POINTS;
      carr[i * 3]     = accent.r * a;
      carr[i * 3 + 1] = accent.g * a;
      carr[i * 3 + 2] = accent.b * a;
    }
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
  });

  return (
    <>
      <group ref={ref}>
        <SatelliteMesh cfg={cfg} pingRef={pingRef} />
      </group>
      {/* @ts-ignore r3f primitive */}
      <line ref={trailRef as any}>
        <primitive object={trailGeom} attach="geometry" />
        <lineBasicMaterial vertexColors transparent opacity={0.7} linewidth={1} />
      </line>
    </>
  );
};


const DataLinks: React.FC<{ shared: SharedRefs }> = ({ shared }) => {
  const groupRef = useRef<THREE.Group>(null);
  const geoms = useMemo(() => {
    return SATS.map(() => {
      const g = new THREE.BufferGeometry();
      const positions = new Float32Array(2 * 3);
      g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      return g;
    });
  }, []);
  const matsRef = useRef<THREE.LineBasicMaterial[]>([]);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    geoms.forEach((g, i) => {
      const p = shared.satPositions.current[i];
      const arr = (g.attributes.position as THREE.BufferAttribute).array as Float32Array;
      arr[0] = 0; arr[1] = 0; arr[2] = 0;
      arr[3] = p.x; arr[4] = p.y; arr[5] = p.z;
      (g.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      g.computeBoundingSphere();
      const m = matsRef.current[i];
      if (m) {
        m.opacity = 0.1 + Math.abs(Math.sin(t * 2.5 + SATS[i].pingOffset)) * 0.35;
      }
    });
  });

  return (
    <group ref={groupRef}>
      {geoms.map((g, i) => (
        // @ts-ignore
        <line key={i}>
          <primitive object={g} attach="geometry" />
          <lineBasicMaterial
            ref={(m) => { if (m) matsRef.current[i] = m; }}
            color={SATS[i].accentColor}
            transparent
            opacity={0.2}
          />
        </line>
      ))}
    </group>
  );
};


const OrbitRings: React.FC = () => {
  const group = useRef<THREE.Group>(null);
  useFrame((state) => {
    if (group.current) {
      group.current.rotation.z = state.clock.getElapsedTime() * 0.04;
    }
  });
  return (
    <group ref={group}>
      {SATS.map((s, i) => (
        <mesh key={i} rotation={[Math.PI / 2 + s.tiltX, 0, s.tiltZ]}>
          <ringGeometry args={[s.radius - 0.01, s.radius + 0.01, 128]} />
          <meshBasicMaterial color="#5ce1e6" transparent opacity={0.08} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
};


const CenterCore: React.FC<{ shared: SharedRefs }> = ({ shared }) => {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.15;
    ref.current.rotation.x = shared.mouse.current.y * 0.3;
    const pulse = 1 + Math.sin(t * 1.5) * 0.05 + shared.easter.current * 0.8;
    ref.current.scale.setScalar(pulse);
  });
  return (
    <mesh ref={ref}>
      <icosahedronGeometry args={[0.9, 1]} />
      <meshStandardMaterial color="#0a2040" emissive="#5ce1e6" emissiveIntensity={0.4} wireframe />
    </mesh>
  );
};


interface AsteroidCfg {
  pos: [number, number, number];
  scale: number;
  rotSpeed: [number, number, number];
}

const Asteroids: React.FC<{ shared: SharedRefs }> = ({ shared }) => {
  const asteroids = useMemo<AsteroidCfg[]>(() => {
    const arr: AsteroidCfg[] = [];
    for (let i = 0; i < 18; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 10 + Math.random() * 18;
      arr.push({
        pos: [Math.cos(angle) * dist, (Math.random() - 0.5) * 10, -5 - Math.random() * 15 + Math.sin(angle) * dist * 0.3],
        scale: 0.2 + Math.random() * 0.7,
        rotSpeed: [Math.random() * 0.3, Math.random() * 0.3, Math.random() * 0.3],
      });
    }
    return arr;
  }, []);

  const groupRef = useRef<THREE.Group>(null);

  useFrame((state, dt) => {
    if (!groupRef.current) return;
    groupRef.current.children.forEach((m, i) => {
      const c = asteroids[i];
      m.rotation.x += c.rotSpeed[0] * dt;
      m.rotation.y += c.rotSpeed[1] * dt;
      m.rotation.z += c.rotSpeed[2] * dt;
    });
    groupRef.current.position.y = -shared.scrollY.current * 4;
    groupRef.current.rotation.y = shared.scrollY.current * 0.2;
  });

  return (
    <group ref={groupRef}>
      {asteroids.map((c, i) => (
        <mesh key={i} position={c.pos} scale={c.scale}>
          <dodecahedronGeometry args={[0.5, 0]} />
          <meshStandardMaterial color="#3a4258" flatShading roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
};


const NebulaVS = `
  varying vec3 vWorldPos;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const NebulaFS = `
  varying vec3 vWorldPos;
  uniform float uTime;

  // simple hash noise
  float hash(vec3 p) { return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }
  float noise(vec3 p) {
    vec3 i = floor(p); vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash(i);
    float n100 = hash(i + vec3(1,0,0));
    float n010 = hash(i + vec3(0,1,0));
    float n110 = hash(i + vec3(1,1,0));
    float n001 = hash(i + vec3(0,0,1));
    float n101 = hash(i + vec3(1,0,1));
    float n011 = hash(i + vec3(0,1,1));
    float n111 = hash(i + vec3(1,1,1));
    return mix(mix(mix(n000,n100,f.x), mix(n010,n110,f.x), f.y),
               mix(mix(n001,n101,f.x), mix(n011,n111,f.x), f.y), f.z);
  }
  float fbm(vec3 p) {
    float v = 0.0; float a = 0.5;
    for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
    return v;
  }

  void main() {
    vec3 p = normalize(vWorldPos);
    float n = fbm(p * 2.5 + vec3(uTime * 0.03, 0.0, uTime * 0.02));
    vec3 deepBlue = vec3(0.03, 0.08, 0.2);
    vec3 teal = vec3(0.05, 0.3, 0.42);
    vec3 nebula = mix(vec3(0.0, 0.01, 0.04), deepBlue, n);
    nebula = mix(nebula, teal, smoothstep(0.55, 0.85, n) * 0.4);
    float fade = smoothstep(-1.0, 1.0, p.y) * 0.6 + 0.4;
    gl_FragColor = vec4(nebula * fade, 1.0);
  }
`;

const Nebula: React.FC = () => {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  useFrame((state) => {
    if (matRef.current) matRef.current.uniforms.uTime.value = state.clock.getElapsedTime();
  });
  return (
    <mesh>
      <sphereGeometry args={[80, 32, 32]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={NebulaVS}
        fragmentShader={NebulaFS}
        side={THREE.BackSide}
        uniforms={{ uTime: { value: 0 } }}
      />
    </mesh>
  );
};


const ShootingStar: React.FC = () => {
  const ref = useRef<THREE.Mesh>(null);
  const state = useRef({ active: false, t: 0, start: new THREE.Vector3(), dir: new THREE.Vector3(), next: 5 });

  useFrame((s, dt) => {
    if (!ref.current) return;
    state.current.t += dt;
    if (!state.current.active && state.current.t > state.current.next) {
      state.current.active = true;
      state.current.t = 0;
      state.current.next = 8 + Math.random() * 6;
      const y = 4 + Math.random() * 3;
      state.current.start.set(-12, y, -5 - Math.random() * 5);
      state.current.dir.set(1, -0.3, 0).normalize();
    }
    if (state.current.active) {
      const prog = state.current.t / 1.4;
      if (prog >= 1) {
        state.current.active = false;
        state.current.t = 0;
        ref.current.visible = false;
        return;
      }
      ref.current.visible = true;
      ref.current.position.copy(state.current.start).addScaledVector(state.current.dir, prog * 25);
      const mat = ref.current.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.sin(prog * Math.PI);
    }
  });

  return (
    <mesh ref={ref} visible={false} rotation={[0, 0, -0.3]}>
      <planeGeometry args={[2, 0.04]} />
      <meshBasicMaterial color="#ffffff" transparent opacity={0} />
    </mesh>
  );
};


const GridVS = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const GridFS = `
  varying vec2 vUv;
  uniform float uTime;
  void main() {
    vec2 uv = vUv;
    uv.y = fract(uv.y * 30.0 + uTime * 0.2);
    uv.x = fract(uv.x * 30.0);
    float l = smoothstep(0.02, 0.0, min(uv.x, uv.y)) + smoothstep(0.98, 1.0, max(uv.x, uv.y));
    float fade = smoothstep(0.0, 0.4, vUv.y) * smoothstep(1.0, 0.4, vUv.y);
    vec3 col = vec3(0.36, 0.88, 0.90) * l * fade;
    gl_FragColor = vec4(col, l * fade * 0.6);
  }
`;

const SynthGrid: React.FC = () => {
  const ref = useRef<THREE.ShaderMaterial>(null);
  useFrame((s) => {
    if (ref.current) ref.current.uniforms.uTime.value = s.clock.getElapsedTime();
  });
  return (
    <mesh position={[0, -6, -8]} rotation={[-Math.PI / 2.3, 0, 0]}>
      <planeGeometry args={[60, 40, 1, 1]} />
      <shaderMaterial
        ref={ref}
        vertexShader={GridVS}
        fragmentShader={GridFS}
        transparent
        side={THREE.DoubleSide}
        uniforms={{ uTime: { value: 0 } }}
      />
    </mesh>
  );
};


const Scene: React.FC<{ shared: SharedRefs }> = ({ shared }) => {
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (!shared.reducedMotion.current) {
      camera.position.x += (shared.mouse.current.x * 1.2 - camera.position.x) * 0.04;
      camera.position.y += (shared.mouse.current.y * 0.8 - camera.position.y) * 0.04;
    }
    camera.lookAt(0, 0, 0);

    if (groupRef.current && !shared.reducedMotion.current) {
      groupRef.current.rotation.y += 0.0008;
      groupRef.current.rotation.x = shared.scrollY.current * 0.5;
    }
  });

  return (
    <>
      <ambientLight intensity={0.35} />
      <pointLight position={[5, 5, 5]} intensity={1.2} color="#5ce1e6" />
      <pointLight position={[-8, -3, -5]} intensity={0.8} color="#1a3a7a" />
      <directionalLight position={[0, 10, 5]} intensity={0.5} />

      <Nebula />
      <Stars radius={60} depth={40} count={3500} factor={3} saturation={0} fade speed={0.6} />
      <Asteroids shared={shared} />
      <ShootingStar />

      <group ref={groupRef}>
        <CenterCore shared={shared} />
        <OrbitRings />
        <DataLinks shared={shared} />
        {SATS.map((cfg, i) => (
          <Float key={i} speed={1.2} rotationIntensity={0.2} floatIntensity={0.2}>
            <Satellite cfg={cfg} index={i} shared={shared} />
          </Float>
        ))}
      </group>
    </>
  );
};


const KONAMI = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];

const SpaceHero: React.FC = () => {
  const mouse = useRef({ x: 0, y: 0 });
  const scrollY = useRef(0);
  const easter = useRef(0);
  const reducedMotion = useRef(false);
  const satPositions = useRef<THREE.Vector3[]>(SATS.map(() => new THREE.Vector3()));
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotion.current = mq.matches;
    const listener = (e: MediaQueryListEvent) => { reducedMotion.current = e.matches; };
    mq.addEventListener('change', listener);

    const onMove = (e: MouseEvent) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    const onScroll = () => {
      const h = window.innerHeight;
      scrollY.current = Math.min(window.scrollY / h, 1.2);
      setVisible(window.scrollY < h * 1.3);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Konami
    let seq: string[] = [];
    const onKey = (e: KeyboardEvent) => {
      seq.push(e.key);
      if (seq.length > KONAMI.length) seq.shift();
      if (seq.length === KONAMI.length && seq.every((k, i) => k === KONAMI[i])) {
        easter.current = 1;
        const start = performance.now();
        const fade = () => {
          const t = (performance.now() - start) / 4000;
          if (t >= 1) { easter.current = 0; return; }
          easter.current = 1 - t;
          requestAnimationFrame(fade);
        };
        requestAnimationFrame(fade);
        seq = [];
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      mq.removeEventListener('change', listener);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const shared: SharedRefs = { mouse, scrollY, easter, reducedMotion, satPositions };

  if (!visible) return null;

  return (
    <Canvas
      className="space-hero-canvas"
      camera={{ position: [0, 0, 10], fov: 55 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <Suspense fallback={null}>
        <Scene shared={shared} />
      </Suspense>
    </Canvas>
  );
};

export default SpaceHero;
